import type { BoxProps } from '@mui/material/Box';

import { Controller, useFormContext } from 'react-hook-form';

import Box from '@mui/material/Box';

import { useTranslate } from 'src/locales';

import { HelperText } from './help-text';
import { Upload, UploadBox, UploadAvatar } from '../upload';

import type { UploadProps } from '../upload';

// ----------------------------------------------------------------------

export type RHFUploadProps = UploadProps & {
  name: string;
  slotProps?: {
    wrapper?: BoxProps;
  };
  uploadPhotoLabel?: string;
  isSmall?: boolean;
};

export function RHFUploadAvatar({
  name,
  slotProps,
  uploadPhotoLabel,
  isSmall,
  ...other
}: RHFUploadProps) {
  const { control, setValue, setError, clearErrors } = useFormContext();
  const { t } = useTranslate('translation');

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const onDrop = (acceptedFiles: File[]) => {
          const file = acceptedFiles[0];
          if (file) {
            const allowedExtensions = ['.png', '.jpg', '.jpeg'];
            const fileExt = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();

            if (!allowedExtensions.includes(fileExt)) {
              setError(name, {
                type: 'manual',
                message: t('ONLY_IMAGE_FILES_ARE_ALLOWED'),
              });
              return;
            }

            clearErrors(name);
            setValue(name, file, { shouldValidate: true });
          }
        };

        return (
          <Box {...slotProps?.wrapper}>
            <UploadAvatar
              value={field.value}
              error={!!error}
              onDrop={onDrop}
              uploadPhotoLabel={uploadPhotoLabel}
              isSmall={isSmall}
              setError={setError}
              name={name}
              {...other}
            />
            <HelperText
              errorMessage={
                error?.message === 'Input not instance of File'
                  ? 'Please upload a file below 3 MB.'
                  : error?.message
              }
              sx={{ textAlign: 'center' }}
            />
          </Box>
        );
      }}
    />
  );
}

// ----------------------------------------------------------------------

export function RHFUploadBox({ name, ...other }: RHFUploadProps) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <UploadBox value={field.value} error={!!error} {...other} />
      )}
    />
  );
}

// ----------------------------------------------------------------------

export function RHFUpload({ name, multiple, helperText, accept, ...other }: RHFUploadProps) {
  const { control, setValue } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const uploadProps = {
          multiple,
          accept: {
            ...accept,
          },
          error: !!error,
          helperText: error?.message ?? helperText,
        };

        const onDrop = (acceptedFiles: File[]) => {
          const value = multiple ? [...field.value, ...acceptedFiles] : acceptedFiles[0];

          setValue(name, value, { shouldValidate: true });
        };

        return <Upload {...uploadProps} value={field.value} onDrop={onDrop} {...other} />;
      }}
    />
  );
}
